"use client";

import { AccessibleOutlineCSS } from "@faire/deprecated-retailer-visitor-shared/components/AccessibleOutlineCSS";
import {
  CTATileData,
  maybeAddCTATile,
} from "@faire/deprecated-retailer-visitor-shared/components/Carousel/CTATile";
import { HorizontalScrollCarousel } from "@faire/deprecated-retailer-visitor-shared/components/Carousel/HorizontalScrollCarousel";
import {
  CarouselDots,
  DotsColorVariant,
} from "@faire/deprecated-retailer-visitor-shared/components/ImageCarousel/CarouselDots";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Grid } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { SpacerValueOrKey } from "@faire/web/slate/spacing/getSpacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import chunk from "lodash/chunk";
import size from "lodash/size";
import times from "lodash/times";
import * as React from "react";
import { useState, type JSX } from "react";
import SwipeableViews from "react-swipeable-views";
import { SlideRenderProps, virtualize } from "react-swipeable-views-utils";
import styled, { css } from "styled-components";

const VirtualizedSwipeableViews = virtualize(SwipeableViews);

interface ICarouselExtraProps {
  isSSR: boolean;
  isMobile: boolean;
}

const CarouselBase = <T,>(props: IProps<T> & ICarouselExtraProps) => {
  const {
    padItems = false,
    peekNext,
    noWrap,
    itemGap = 20,
    peekGapPercent = 10,
    enableMouseEvents,
    useHorizontalScrollCarousel,
    swipeableLeftPadding,
    disableScrollWhenSingleSlide,
    alwaysIncludePeek,
    onVisibile,
  } = props;

  const viewRef = useDoActionWhenVisible(onVisibile);
  const [slideIndex, setSlideIndex] = useState(0);
  const [overscanBefore, setOverscanBefore] = useState(
    noWrap || isPrerender() ? 0 : 1
  );
  const [overscanAfter, setOverscanAfter] = useState(1);

  const items = maybeAddCTATile(
    props.items,
    props.ctaTileData,
    props.itemsPerSlide
  );

  const itemsPerSlide = padItems
    ? props.itemsPerSlide
    : Math.min(props.itemsPerSlide, items?.length);

  const slideCount = Math.ceil(size(items) / itemsPerSlide);

  const updateOverscanNext = () => {
    if (slideIndex >= slideCount - 2) {
      setOverscanAfter(0);
    } else {
      setOverscanAfter(1);
    }
    setOverscanBefore(1);
  };
  const updateOverscanBefore = () => {
    if (slideIndex <= 1) {
      setOverscanBefore(0);
    } else {
      setOverscanBefore(1);
    }
    setOverscanAfter(1);
  };

  const handleChangeSlideIndex = (inSlideIndex: number) => {
    if (inSlideIndex > slideIndex) {
      showNextTiles();
    } else if (inSlideIndex < slideIndex) {
      showPreviousTiles();
    }
  };

  const handleChangeSlideIndexFromDot = (inSlideIndex: number) => {
    // The carousel tracks indices in a wrapping carousel as tiles extending
    // off to infinity, i.e. even if there are 3 pages,
    // you can have an index of 5 or 500 if you keep clicking next.
    const loopedTargetPosition = positiveMod(inSlideIndex, slideCount);
    const loopedCurrentPosition = positiveMod(slideIndex, slideCount);
    if (loopedTargetPosition === loopedCurrentPosition) {
      return;
    }

    if (props.noWrap) {
      setSlideIndex(loopedTargetPosition);
      trackScrollEvent(
        loopedTargetPosition < loopedCurrentPosition ? "previous" : "next",
        loopedTargetPosition
      );
    } else {
      // When the user clicks on a dot below the carousel, we
      // want to scroll in the direction that the user expects -- so if the dot
      // they clicked on is to the left of the active dot, we should scroll to
      // the nearest index that is to the left of the active index.
      //
      // This logic selects that index.
      const shouldScrollLeft = loopedTargetPosition < loopedCurrentPosition;
      const leftEdge = roundDown(slideIndex, slideCount);
      let targetIndex = leftEdge + loopedTargetPosition;
      if (shouldScrollLeft && targetIndex > slideIndex) {
        targetIndex -= slideCount;
      } else if (!shouldScrollLeft && targetIndex < slideIndex) {
        targetIndex += slideCount;
      }
      setSlideIndex(targetIndex);
      trackScrollEvent(
        shouldScrollLeft ? "previous" : "next",
        loopedTargetPosition
      );
    }
  };

  const showNextTiles = () => {
    const targetIndex = slideIndex + 1;
    if (props.noWrap && targetIndex >= slideCount) {
      return;
    }
    setSlideIndex(targetIndex);
    if (targetIndex === slideCount - 1) {
      trackEvent(`/${props.name}/carousel/lastitems`, true);
    }
    if (noWrap) {
      updateOverscanNext();
    }
    trackScrollEvent("next", targetIndex);
  };

  const showPreviousTiles = () => {
    const targetIndex = slideIndex - 1;
    if (props.noWrap && targetIndex < 0) {
      return;
    }
    setSlideIndex(targetIndex);
    if (targetIndex === slideCount - 1) {
      trackEvent(`/${props.name}/carousel/lastitems`, true);
    }
    if (noWrap) {
      updateOverscanBefore();
    }
    trackScrollEvent("previous", targetIndex);
  };

  const trackScrollEvent = (
    direction: "next" | "previous",
    targetIndex: number
  ) => {
    if (props.onScroll) {
      props.onScroll(direction, targetIndex % slideCount, targetIndex);
    } else {
      trackEvent(`/${props.name}/carousel/${direction}`, true);
    }
  };

  const computeSlideItems = (inSlideIndex: number) => {
    if (slideCount === 0) {
      return [];
    }

    const start = inSlideIndex * itemsPerSlide;
    let itemsThisSlide = times(itemsPerSlide).map((i) =>
      getVirtualItem(start + i)
    );

    if (props.noWrap && inSlideIndex + 1 === slideCount) {
      itemsThisSlide = itemsThisSlide.slice(
        0,
        items.length - props.itemsPerSlide * inSlideIndex
      );
    }

    return itemsThisSlide;
  };

  const shouldPadItem = (tileIndex: number) => {
    return (
      padItems &&
      props.itemsPerSlide > items.length &&
      tileIndex >= items.length
    );
  };

  const renderItem = (
    item: T | React.ReactElement,
    tileIndex: number
  ): React.ReactNode => {
    if (shouldPadItem(tileIndex)) {
      return <PaddedItem key={`carousel-padded-item-${tileIndex}`} />;
    }

    if (React.isValidElement(item)) {
      return item;
    }

    return props.renderItem(item as T, tileIndex, slideIndex);
  };

  const slideRenderer = ({ key, index }: SlideRenderProps) => {
    const slideItems = computeSlideItems(index);
    /* don't render slides that are not in the view with SSR */
    if (props.isSSR && index !== slideIndex) {
      return <React.Fragment key={key} />;
    }
    return (
      <StyledGrid
        key={key}
        gap={itemGap}
        columns={itemsPerSlide}
        $padding={
          props.peekNext
            ? (props.peekPadding ?? `0 ${(itemGap || 0) / 2}px`)
            : undefined
        }
      >
        {slideItems.map(renderItem)}
      </StyledGrid>
    );
  };

  const getVirtualItem = (virtualIndex: number): T | JSX.Element => {
    let index = virtualIndex % size(items);
    index = index >= 0 ? index : size(items) + index;
    return items[index] ?? <></>;
  };

  const renderNonVirtualizedSlides = (): JSX.Element[] => {
    const chunks = chunk(items, itemsPerSlide);
    return chunks.map((slideChunk, chunkIndex) => {
      /* don't render slides that are not in the view with SSR */
      if (props.isSSR && chunkIndex !== slideIndex) {
        return <></>;
      }

      const padding = props.removePaddingFromFirstAndLastItems
        ? "0"
        : `0 ${(itemGap || 0) / 2}px`;

      return (
        <StyledGrid
          key={chunkIndex}
          columns={itemsPerSlide}
          gap={itemGap}
          $padding={padding}
        >
          {slideChunk.map((item, index) => renderItem(item, index))}
        </StyledGrid>
      );
    });
  };

  const renderCarousel = () => {
    const swipeablePadding = `0 ${peekGapPercent}% 0 ${
      swipeableLeftPadding ?? itemGap / 2 - 1
    }px`;
    if (peekNext && noWrap) {
      if (props.isMobile || useHorizontalScrollCarousel) {
        return (
          <HorizontalScrollCarousel
            dataTestId="horizontal-scroll-carousel"
            items={props.items}
            itemGap={itemGap}
            itemsPerSlide={itemsPerSlide}
            renderItem={props.renderItem}
            peekGapPercent={peekGapPercent}
            lateralPadding={props.lateralPadding}
            onScroll={props.onMobileScroll}
            onScrollEnd={props.onMobileScrollEnd}
            ctaTileData={props.ctaTileData}
          />
        );
      }

      return (
        <StyledSwipeableViews
          index={slideIndex}
          onChangeIndex={handleChangeSlideIndex}
          $slideCount={slideCount}
          $swipeablePadding={swipeablePadding}
          $alwaysIncludePeek={alwaysIncludePeek}
          enableMouseEvents={enableMouseEvents}
          containerStyle={{
            transition: "transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s",
          }}
        >
          {renderNonVirtualizedSlides()}
        </StyledSwipeableViews>
      );
    }

    const paddingForVirtualized =
      peekNext && itemsPerSlide < items.length ? swipeablePadding : undefined;
    return (
      <StyledVirtualizedSwipeableViews
        $swipeablePadding={paddingForVirtualized}
        index={slideIndex}
        onChangeIndex={handleChangeSlideIndex}
        slideRenderer={slideRenderer}
        enableMouseEvents={enableMouseEvents}
        disabled={disableScrollWhenSingleSlide && slideCount <= 1}
        overscanSlideAfter={isPrerender() ? slideCount : overscanAfter}
        overscanSlideBefore={overscanBefore}
        data-test-id="carousel"
      />
    );
  };

  const renderButtons = () => {
    if (props.useHorizontalScrollCarousel || slideCount <= 1) {
      return null;
    }

    const isFirstSlide = slideIndex === 0;
    const isLastSlide = slideIndex === slideCount - 1;

    return (
      <>
        {props.noWrap && isFirstSlide ? null : (
          <PreviousButton
            onClick={showPreviousTiles}
            data-test-id="btn-previous"
            aria-label="Previous Carousel Slide"
            $offsetTop={props.buttonsOffsetTop}
            $offsetX={props.buttonsOffsetX}
            $showOnMobile={props.shouldShowArrowsOnMobile ?? false}
          >
            <ButtonWrapper>
              <Chevron direction="left" color={props.buttonsColor} />
            </ButtonWrapper>
          </PreviousButton>
        )}
        {props.noWrap && isLastSlide ? null : (
          <NextButton
            onClick={showNextTiles}
            data-test-id="btn-next"
            aria-label="Next Carousel Slide"
            $offsetTop={props.buttonsOffsetTop}
            $offsetX={props.buttonsOffsetX}
            $showOnMobile={props.shouldShowArrowsOnMobile ?? false}
          >
            <ButtonWrapper>
              <Chevron direction="right" color={props.buttonsColor} />
            </ButtonWrapper>
          </NextButton>
        )}
      </>
    );
  };

  if (isNaN(slideCount) || !slideCount) {
    return null;
  }

  return (
    <Wrapper ref={viewRef} className={props.className}>
      {renderCarousel()}
      {renderButtons()}
      {props.shouldShowDots ? (
        <>
          <Spacer size={props.dotsOffsetY ?? "5x"} />
          <CarouselDots
            onClick={(index) => {
              handleChangeSlideIndexFromDot(index);
            }}
            currentImageIndex={positiveMod(slideIndex, slideCount)}
            numValidImages={slideCount}
            dotSize="large"
            colorVariant={props.dotsColorVariant}
          />
        </>
      ) : null}
    </Wrapper>
  );
};

const positiveMod = (dividend: number, divisor: number): number =>
  ((dividend % divisor) + divisor) % divisor;

const roundDown = (value: number, toMultipleOf: number): number =>
  value - positiveMod(value, toMultipleOf);

export const ButtonWrapper = styled.div``;

/**
 * @deprecated use @faire/web/ui-labs/Carousel
 */
export const Carousel = <T,>(props: IProps<T>) => {
  const isSSR = useIsSSR();
  const { isMobile } = useViewport();
  return <CarouselBase {...props} isSSR={isSSR} isMobile={isMobile} />;
};

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const PaddedItem = styled.div``;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/IconButton/IconButton#IconButton"
 */
const Button = styled.button<{ $offsetTop?: string }>`
  position: absolute;
  top: ${(props) => props.$offsetTop ?? "50%"};

  ${AccessibleOutlineCSS}
  padding: 4px 0;
  border-radius: 8px; // make the focus outline rounded

  & > div {
    transition: transform 0.4s ease;
  }
`;

export const PreviousButton = styled(Button)<{
  $offsetX?: string;
  $showOnMobile: boolean;
}>`
  left: 0;
  transform: translate(
    calc((100% + ${(props) => props.$offsetX ?? "16px"}) * -1),
    -50%
  );

  &:hover > div {
    transform: translateX(-4px);
  }

  ${({ $showOnMobile }) =>
    !$showOnMobile &&
    css`
      ${responsiveMediaQueries.mobile} {
        display: none;
      }
    `}
`;

export const NextButton = styled(Button)<{
  $offsetX?: string;
  $showOnMobile: boolean;
}>`
  right: 0;
  transform: translate(
    calc(100% + ${(props) => props.$offsetX ?? "16px"}),
    -50%
  );

  &:hover > div {
    transform: translateX(4px);
  }

  ${({ $showOnMobile }) =>
    !$showOnMobile &&
    css`
      ${responsiveMediaQueries.mobile} {
        display: none;
      }
    `}
`;

const StyledSwipeableViews = styled(SwipeableViews)<{
  $slideCount: number;
  $swipeablePadding: string;
  $alwaysIncludePeek?: boolean;
}>`
  width: 100%;
  ${({ $slideCount, $swipeablePadding, $alwaysIncludePeek }) =>
    $slideCount > 1 || $alwaysIncludePeek
      ? css`
          padding: ${$swipeablePadding};
        `
      : ""}
`;

const StyledVirtualizedSwipeableViews = styled(VirtualizedSwipeableViews)<{
  $swipeablePadding?: string;
}>`
  width: 100%;
  // this clip is added to prevent the bottom border from being hidden
  // the important is added because the third party swipeableViews adds overflow-x: hidden property to the element
  overflow-x: clip !important;
  ${({ $swipeablePadding }) =>
    $swipeablePadding
      ? css`
          padding: ${$swipeablePadding};
        `
      : ""}
`;

const StyledGrid = styled(Grid).attrs({
  columnTemplate: (c: number) => `repeat(${c}, minmax(0, 1fr));`,
})<{ $padding?: string }>`
  height: 100%;
  ${(props) =>
    props.$padding
      ? css`
          padding: ${props.$padding};
        `
      : ""}
`;

// eslint-disable-next-line @faire/no-re-exports
export const CarouselHorizontalScrollCarouselItem =
  HorizontalScrollCarousel.Item;

export interface IProps<T> {
  ctaTileData?: CTATileData;
  itemsPerSlide: number;
  items: ReadonlyArray<T>;
  renderItem: (item: T, index: number, slideIndex?: number) => React.ReactNode;
  /**
   * Defaults to 20
   */
  itemGap?: number;
  padItems?: boolean;
  name?: string;
  /**
   * @arg targetIndex reflects the number of times the next button has been hit. It loops back to 0 when the first set of slides are shown again.
   */
  onScroll?: (
    direction: "next" | "previous",
    slideIndex: number,
    targetIndex: number
  ) => void;
  onMobileScroll?: (start: number, end: number) => void;
  onMobileScrollEnd?: () => void;
  noWrap?: boolean;
  peekNext?: boolean;
  /**
   * This is percentage of the screen and not of the next tile.
   * i.e peekGapPercent={40} will make 40% of the screen for the peeked tile
   * while 60% of the screen will be left available for the rest of the tiles
   * Defaults to 10
   */
  peekGapPercent?: number;
  className?: string;
  enableMouseEvents?: boolean;
  useHorizontalScrollCarousel?: boolean;
  /**
   * Optional prop to change buttons color.
   * Defaults to faireBlack #333333
   */
  buttonsColor?: string;
  /**
   * Optional prop to control button position relative to top of tile.
   * Defaults to 50%
   */
  buttonsOffsetTop?: string;
  /**
   * Options prop to control space between leftmost tile and previous arrow button,
   * and between rightmost tile and next arrow button.
   */
  buttonsOffsetX?: string;
  /**
   * Padding given to Horizontal Scroll View, used to align carousel items
   * with other content
   * Defaults to 20
   */
  lateralPadding?: number;
  /**
   * Padding between carousel items and peeked item
   */
  peekPadding?: string;
  /**
   * Left Padding of swipeable content of the carousel
   */
  swipeableLeftPadding?: number;
  /**
   * By default, each item of the horizontal carousel will have a padding of half
   * the distance between two items in each side, including the first and last
   * items.
   * Setting this prop true will remove the left padding of the first item and the
   * right padding of the last item, making it easier to align the horizontal
   * carousel.
   * Defaults to false
   */
  removePaddingFromFirstAndLastItems?: boolean;
  /**
   * when peekNext is set to true and there are more items than itemsPerSlide,
   * it will show a portion of the next item, If this boolean is true, even if
   * items is less than itemsPerSlide, the peek padding will still be included
   */
  alwaysIncludePeek?: boolean;
  disableScrollWhenSingleSlide?: boolean;
  /**
   * If true, then the carousel will have dots beneath it showing which slide is
   * active.
   */
  shouldShowDots?: boolean;
  /**
   * The colour scheme for the dots, per `CarouselDots`.
   */
  dotsColorVariant?: DotsColorVariant;
  /**
   * The vertical offset of the dots from the bottom of the carousel.
   */
  dotsOffsetY?: ResponsiveProp<SpacerValueOrKey>;
  /**
   * Shows arrows on mobile
   */
  shouldShowArrowsOnMobile?: boolean;
  onVisibile?: () => void;
}
